import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesAPI, vendorsAPI, currenciesAPI } from '../api/client';
import apiClient from '../api/client';
import { useAuth } from '../context/AuthContext';
import { isDepartmentManager } from '../utils/roleHelpers';

export default function InvoiceCreate() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDeptManager = isDepartmentManager(user);
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formData, setFormData] = useState({
    invoiceNumber: '',
    vendorId: '',
    vendorName: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    currency: 'GBP',
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    paymentTerms: '',
    notes: '',
    purchaseOrder: '',
    lines: []
  });

  useEffect(() => {
    fetchVendors();
    fetchCurrencies();
  }, []);

  const fetchVendors = async () => {
    try {
      let response;
      if (isDeptManager) {
        response = await apiClient.get('/vendors/my-department');
        const vendorList = response.data.vendors || response.data || [];
        setVendors(Array.isArray(vendorList) ? vendorList : []);
      } else {
        response = await vendorsAPI.getAll();
        const vendorList = Array.isArray(response) ? response : (response.data?.vendors || response.data || []);
        setVendors(vendorList);
      }
    } catch (error) {
      console.error('Fetch vendors error:', error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await currenciesAPI.getAll();
      const currencyList = Array.isArray(response) ? response : (response.data?.currencies || response.data || []);
      setCurrencies(currencyList);
    } catch (error) {
      console.error('Fetch currencies error:', error);
    }
  };

  const addLine = () => {
    setFormData(prev => ({
      ...prev,
      lines: [...prev.lines, { 
        lineNumber: prev.lines.length + 1,
        description: '', 
        quantity: 1, 
        unitPrice: 0, 
        lineTotal: 0 
      }]
    }));
  };

  const removeLine = (index) => {
    if (!confirm('Remove this line item?')) return;
    
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== index).map((line, idx) => ({
        ...line,
        lineNumber: idx + 1
      }))
    }));
  };

  const updateLine = (index, field, value) => {
    setFormData(prev => {
      const updatedLines = prev.lines.map((line, i) => {
        if (i !== index) return line;
        
        const updated = { ...line, [field]: parseFloat(value) || 0 };
        
        // Auto-calculate line total
        if (field === 'quantity' || field === 'unitPrice') {
          updated.lineTotal = (updated.quantity || 0) * (updated.unitPrice || 0);
        }
        
        return updated;
      });
      
      return { ...prev, lines: updatedLines };
    });
  };

  const updateLineDescription = (index, value) => {
    setFormData(prev => ({
      ...prev,
      lines: prev.lines.map((line, i) => 
        i === index ? { ...line, description: value } : line
      )
    }));
  };

  // Auto-calculate totals when lines change
  useEffect(() => {
    const subtotal = formData.lines.reduce((sum, line) => sum + (parseFloat(line.lineTotal) || 0), 0);
    const total = subtotal + (parseFloat(formData.taxAmount) || 0);
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      totalAmount: total
    }));
  }, [formData.lines, formData.taxAmount]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadedFile(file);
    }
  };

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.CurrencyCode === code);
    return currency?.Symbol || '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vendorId) {
      alert('Please select a vendor');
      return;
    }
    
    if (!formData.invoiceDate) {
      alert('Please enter an invoice date');
      return;
    }
    
    if (!formData.currency) {
      alert('Please select a currency');
      return;
    }
    
    if (formData.lines.length === 0) {
      alert('Please add at least one line item');
      return;
    }

    try {
      setLoading(true);
      
      const submitData = {
        InvoiceNumber: formData.invoiceNumber,
        VendorID: formData.vendorId,
        InvoiceDate: formData.invoiceDate,
        DueDate: formData.dueDate || null,
        Currency: formData.currency,
        SubTotal: parseFloat(formData.subtotal) || 0,
        TaxAmount: parseFloat(formData.taxAmount) || 0,
        TotalAmount: parseFloat(formData.totalAmount) || 0,
        PaymentTerms: formData.paymentTerms,
        Notes: formData.notes,
        PurchaseOrder: formData.purchaseOrder,
        IsManualEntry: true, // Flag as manually created
        lines: formData.lines.map((line, index) => ({
          LineNumber: index + 1,
          Description: line.description,
          Quantity: parseFloat(line.quantity) || 1,
          UnitPrice: parseFloat(line.unitPrice) || 0,
          Amount: parseFloat(line.lineTotal) || 0,
          LineTotal: parseFloat(line.lineTotal) || 0
        }))
      };

      const response = await invoicesAPI.create(submitData);
      
      // If file was uploaded, attach it to the invoice
      if (uploadedFile && response.data?.invoiceId) {
        // TODO: Implement file upload to blob storage
        console.log('File upload for manual invoices to be implemented');
      }
      
      alert('Invoice created successfully!');
      navigate(`/invoices/${response.data.invoiceId}`);
    } catch (error) {
      console.error('Create invoice error:', error);
      alert('Failed to create invoice: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getValidationWarnings = () => {
    const warnings = [];
    if (!formData.vendorId) warnings.push('Vendor is required');
    if (!formData.invoiceDate) warnings.push('Invoice date is required');
    if (!formData.currency) warnings.push('Currency is required');
    if (formData.lines.length === 0) warnings.push('At least one line item is required');
    return warnings;
  };

  const validationWarnings = getValidationWarnings();

  return (
    <div className="space-y-6">
      {/* Header with Badge */}
      <div className="flex justify-between items-center">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-gray-900">Create New Invoice</h1>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              📝 Manual Entry
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Manually enter invoice details and optionally attach a document
          </p>
        </div>
        <button
          onClick={() => navigate('/invoices')}
          className="btn btn-secondary"
        >
          Cancel
        </button>
      </div>

      {/* Validation Warnings */}
      {validationWarnings.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-md p-4">
          <div className="flex items-start gap-2">
            <span className="text-red-600 text-lg">⚠️</span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900">Required Fields Missing:</h3>
              <ul className="mt-2 text-sm text-red-800 list-disc list-inside">
                {validationWarnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invoice Header */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Invoice Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Number
              </label>
              <input
                type="text"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., INV-2026-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Vendor <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.vendorId}
                onChange={(e) => setFormData(prev => ({ ...prev, vendorId: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !formData.vendorId ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select Vendor...</option>
                {vendors.map(vendor => (
                  <option key={vendor.VendorID} value={vendor.VendorID}>
                    {vendor.VendorName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !formData.invoiceDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  !formData.currency ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
                required
              >
                <option value="">Select Currency...</option>
                {currencies.map(curr => (
                  <option key={curr.CurrencyCode} value={curr.CurrencyCode}>
                    {curr.Symbol} {curr.CurrencyCode} - {curr.CurrencyName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Order
              </label>
              <input
                type="text"
                value={formData.purchaseOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, purchaseOrder: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="PO Number (optional)"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Terms
              </label>
              <input
                type="text"
                value={formData.paymentTerms}
                onChange={(e) => setFormData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Net 30"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Line Items</h2>
            <button
              type="button"
              onClick={addLine}
              className="btn btn-primary"
            >
              + Add Line Item
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Line Total</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {formData.lines.map((line, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-sm text-gray-900">{index + 1}</td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={line.description}
                        onChange={(e) => updateLineDescription(index, e.target.value)}
                        placeholder="Item description"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        value={line.quantity}
                        onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                        className="w-20 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        step="0.01"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(index, 'unitPrice', e.target.value)}
                        className="w-28 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-right font-medium text-gray-900">
                      {getCurrencySymbol(formData.currency)}{(line.lineTotal || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="text-red-600 hover:text-red-700 px-2 py-1"
                        title="Remove line"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {formData.lines.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No line items yet. Click "+ Add Line Item" to get started.
              </div>
            )}
          </div>

          {/* Totals */}
          <div className="flex justify-end pt-4 border-t">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium text-gray-900">
                  {getCurrencySymbol(formData.currency)}{formData.subtotal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax:</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.taxAmount}
                  onChange={(e) => setFormData(prev => ({ ...prev, taxAmount: e.target.value }))}
                  className="w-32 px-2 py-1 text-sm text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span className="text-gray-900">Total:</span>
                <span className="text-blue-600">
                  {getCurrencySymbol(formData.currency)}{formData.totalAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Additional Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes or comments..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Attach Document (Optional)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100"
              />
              {uploadedFile && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  ✓ {uploadedFile.name}
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Upload a PDF copy of the invoice document (max 10MB)
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/invoices')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || validationWarnings.length > 0}
          >
            {loading ? '💾 Creating...' : '💾 Create Invoice'}
          </button>
        </div>
      </form>
    </div>
  );
}
